import { defineStore } from 'pinia'
import { useUserStore } from './user'
import { NotificationDTO, useNotificationStore } from './notification'
import router from '@/router'

import { ILoginResultDTO } from '@/types/auth'
import { AuthenticationApi, buildLocalization, DEBUG, DEBUGC, DEBUG_WARN } from '@/util'
import { HttpStatusCode } from 'axios'

const TOKEN_REFRESH_ENABLED = true
const MIN_TOKEN_LIFETIME_IN_SECONDS_BEFORE_TOKEN_REFRESH = 120 /* seconds */

/** session store, handling access+refresh token logic */
export const useSessionStore = defineStore('sessionStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    let _refreshPromise: Promise<string | null> | null = null
    const state = { }

    /********************************************************************************************************************
     GETTERS
     ********************************************************************************************************************/
    const getters = { }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/

    function isPartnerLogin(): boolean | null {
        const value = sessionStorage.getItem("isPartnerLogin")
        return value == null
            ? null
            : value == "1"
    }

    function getDeviceSerialNo(): string | null {
        const value = sessionStorage.getItem("SN")
        return value
    }

    function setPartnerLogin(data: ILoginResultDTO) {
        setLogin(data, true)
    }

    function setDeviceLogin(data: ILoginResultDTO, serialNo: string) {
        setLogin(data, false)
        setDeviceSerialNo(serialNo)
    }

    async function isAuthenticated(): Promise < boolean > {
        const token = await getValidToken()
        return token != null
    }

    async function getValidToken(): Promise<string | null> {
        let refreshPromise = _refreshPromise

        if (refreshPromise != null) {
            return await refreshPromise
        }

        // Check: token present?
        let accessToken = getAccessToken()
        const expires = getExpires()

        if (accessToken == null || expires == null) return null

        const now = new Date()
        const timeLeftInSeconds = (expires.getTime() - now.getTime()) / 1000

        DEBUG(`Token lifetime: ${timeLeftInSeconds}s`)

        if (TOKEN_REFRESH_ENABLED) {
            // Check: token expired?
            if (timeLeftInSeconds < MIN_TOKEN_LIFETIME_IN_SECONDS_BEFORE_TOKEN_REFRESH) {
                refreshPromise = _refreshPromise

                if (refreshPromise == null) {
                    _refreshPromise = doRefreshToken(accessToken)
                        .finally(() => _refreshPromise = null)

                }

                accessToken = await _refreshPromise
            }
        }
        else {
            return timeLeftInSeconds > 0
                ? accessToken
                : null
        }

        return accessToken
    }

    function logOff() {
        DEBUG('LogOff: Clearing session')
        sessionStorage.clear()

        const userStore = useUserStore()
        userStore.reset()

        router.push("/auth")
    }

    const actions = { isPartnerLogin, setPartnerLogin, getDeviceSerialNo, setDeviceLogin, isAuthenticated, getValidToken, logOff }

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/
    function setLogin(data: ILoginResultDTO, isPartnerLogin: boolean) {
        setTokenData(data)
        setIsPartnerLogin(isPartnerLogin)

        const userStore = useUserStore()
        userStore.refresh()
    }   
    function setTokenData(data: ILoginResultDTO) {
        DEBUGC('setTokenData: setting new token data', 'pink')

        setAccessToken(data.accessToken)
        setRefreshToken(data.refreshToken)
        setExpires(new Date(Date.parse(data.expiresIsoString)))
    }

    function setDeviceSerialNo(sn: string | null) {
        if (sn == null) {
            sessionStorage.removeItem("SN")
        }
        else {
            sessionStorage.setItem("SN", sn)
        }
    }

    function setIsPartnerLogin(isPartnerLogin: boolean | null) {
        if (isPartnerLogin == null) {
            sessionStorage.removeItem("isPartnerLogin")
        }
        else {
            sessionStorage.setItem("isPartnerLogin", isPartnerLogin ? "1" : "0")
        }
    }

    function getAccessToken(): string | null {
        return sessionStorage.getItem("accessToken")
    }

    function setAccessToken(token: string | null) {
        if (token == null) {
            sessionStorage.removeItem("accessToken")
        }
        else {
            sessionStorage.setItem("accessToken", token)
        }
    }

    function getRefreshToken(): string | null {
        return sessionStorage.getItem("refreshToken")
    }

    function setRefreshToken(token: string | null) {
        if (token == null) {
            sessionStorage.removeItem("refreshToken")
        }
        else {
            sessionStorage.setItem("refreshToken", token)
        }       
    }

    function getExpires(): Date | null {
        const expireString = sessionStorage.getItem("expires")
        if (expireString == null) return null

        const expireDate = new Date(expireString)
        if (isNaN(expireDate.valueOf())) return null

        return expireDate
    }

    function setExpires(when: Date | null) {
        if (when == null) {
            sessionStorage.removeItem("expires")
        }
        else {
            sessionStorage.setItem("expires", when.toISOString())
        }
    }
    async function doRefreshToken(accessToken: string): Promise<string | null> {
        const refreshToken = getRefreshToken()
        if (refreshToken == null) {
            DEBUG_WARN('No refresh-token found!')
            return null
        }

        setRefreshToken(null)

        try {
            const api = new AuthenticationApi()

            const response = await api.refreshToken(accessToken, refreshToken)
            if (response.status != HttpStatusCode.Ok) return null

            const newLoginData = response.data

            setTokenData(newLoginData)
            return newLoginData.accessToken
        }
        catch (err) {
            DEBUG_WARN('Refreshing token failed', err, accessToken, refreshToken)
            redirectToLogonPage()
            return null
        }
    }

    function redirectToLogonPage() {
        const notificationStore = useNotificationStore()
        const { localize } = buildLocalization()

        if (isPartnerLogin()) {
            router.replace({ name: 'partner-login', query: { return: router.currentRoute.value.fullPath } })
        }
        else {
            router.replace({ name: 'device-login' } )
        }

        notificationStore.addNotification(new NotificationDTO(localize('sessionHasExpired'), 'warning', 5000))

        sessionStorage.clear()
    }

    /********************************************************************************************************************
     INTERFACE
     ********************************************************************************************************************/
    return { ...state, ...getters, ...actions }
})